import ClusterDisplay from "../ClusterDisplay";
import type { Cluster, MCQConfig, PolarConfig, Question, Response } from "../../lib/types";
import type { StageViewDef } from "../../lib/stageViews";
import { compareModel, mcqModel, polarModel, scaleModel } from "../../lib/stageViewModels";
import { groupByCohort } from "../../lib/cohorts";
import type { SegmentStats } from "../../hooks/useSegmentStats";
import { McqBars } from "./views/McqBars";
import { McqDots } from "./views/McqDots";
import { ScaleDistribution } from "./views/ScaleDistribution";
import { ScaleSentiment } from "./views/ScaleSentiment";
import { PolarScale } from "./views/PolarScale";
import { PolarDots } from "./views/PolarDots";
import { CompareBars } from "./views/CompareBars";
import { CompareSentiment } from "./views/CompareSentiment";
import { OpenVoices } from "./views/OpenVoices";
import { OpenQuotes } from "./views/OpenQuotes";
import { CohortClusterColumns } from "./views/CohortClusterColumns";

type Props = {
  question: Question;
  segmentQuestion: Question | null;
  responses: Response[];
  clusters: Cluster[];
  responsesByCluster: Map<string, Response[]>;
  stats: SegmentStats;
  view: StageViewDef;
  quoteIndex: number;
  onQuoteStep?: (dir: 1 | -1) => void;
  registerClusterRef?: (id: string, el: HTMLElement | null) => void;
  pulseClusterId?: string | null;
  /** Dim the themes grid while a recluster is in flight. */
  dimmed?: boolean;
  registerCardRef?: (id: string, el: HTMLElement | null) => void;
};

/** Renders exactly one stage view for the question. Models are built here so
 *  the view components stay layout-only. */
export default function StageResultsPanel(p: Props) {
  const { question, view, responses, clusters, stats } = p;
  const base = stats.error && stats.respondents === 0 ? null : { respondents: stats.respondents, unlinked: stats.unlinked };
  const compare = compareModel(p.segmentQuestion, stats.segments);

  switch (question.question_type) {
    case "multiple_choice": {
      const model = mcqModel(question, responses, base);
      if (view.key === "dots") return <McqDots model={model} />;
      if (view.key === "compare") {
        const cfg = question.config as MCQConfig;
        const options = [...cfg.options, ...(cfg.allow_other ? [{ key: "__other__", label: cfg.other_label }] : [])];
        return <CompareBars compare={compare} options={options} loading={stats.loading || stats.error} />;
      }
      const cfg = question.config as MCQConfig;
      const hasOtherClusters = clusters.some((c) => c.cohort === "other");
      if (hasOtherClusters) {
        const otherCount = groupByCohort(question, responses).other?.length ?? 0;
        return (
          <div className="flex flex-col h-full gap-6 min-h-0">
            <div className="flex-1 min-h-0">
              <McqBars model={model} />
            </div>
            <div className="flex-shrink-0 max-h-[45%] min-h-0 flex flex-col">
              <CohortClusterColumns
                columns={[{ cohort: "other", title: `Aus „${cfg.other_label}“ ergeben sich`, count: otherCount, accent: true }]}
                clusters={clusters}
              />
            </div>
          </div>
        );
      }
      return <McqBars model={model} />;
    }
    case "likert":
    case "nps": {
      const model = scaleModel(question, responses);
      if (view.key === "sentiment") return <ScaleSentiment model={model} />;
      if (view.key === "compare") return <CompareSentiment compare={compare} question={question} loading={stats.loading || stats.error} />;
      return <ScaleDistribution model={model} question={question} clusters={clusters} />;
    }
    case "polar": {
      const model = polarModel(question, responses);
      if (view.key === "dots") return <PolarDots model={model} />;
      if (view.key === "compare") {
        const cfg = question.config as PolarConfig;
        return <CompareBars compare={compare} options={[{ key: cfg.side_a.key, label: cfg.side_a.label }, { key: cfg.side_b.key, label: cfg.side_b.label }]} loading={stats.loading || stats.error} />;
      }
      const cfg = question.config as PolarConfig;
      const hasSideClusters = clusters.some((c) => c.cohort === "side_a" || c.cohort === "side_b");
      if (hasSideClusters) {
        const groups = groupByCohort(question, responses);
        return (
          <div className="flex flex-col h-full gap-6 min-h-0">
            <div className="flex-1 min-h-0">
              <PolarScale model={model} />
            </div>
            <div className="flex-shrink-0 max-h-[45%] min-h-0 flex flex-col">
              <CohortClusterColumns
                columns={[
                  { cohort: "side_a", title: `${cfg.side_a.label} sagen`, count: groups.side_a?.length ?? 0, accent: true },
                  { cohort: "side_b", title: `${cfg.side_b.label} sagen`, count: groups.side_b?.length ?? 0, accent: false },
                ]}
                clusters={clusters}
              />
            </div>
          </div>
        );
      }
      return <PolarScale model={model} />;
    }
    case "open_text":
    default: {
      if (view.key === "quotes") return <OpenQuotes responses={responses} index={p.quoteIndex} onStep={p.onQuoteStep} />;
      if (view.key === "voices" || clusters.length === 0) return <OpenVoices responses={responses} question={question} registerCardRef={p.registerCardRef} />;
      return (
        <div className={`h-full transition-opacity duration-300 ${p.dimmed ? "opacity-25" : "opacity-100"}`}>
          <ClusterDisplay clusters={clusters} responsesByCluster={p.responsesByCluster} isFrozen={question.is_frozen} registerClusterRef={p.registerClusterRef} pulseClusterId={p.pulseClusterId} />
        </div>
      );
    }
  }
}
